import * as THREE from 'three';

/** Animation part identifiers baked into vertex attributes. */
export const enum Part {
  Static = 0,
  LegL = 1,
  LegR = 2,
  ArmR = 3,
  ArmL = 4,
  HorseFL = 5,
  HorseFR = 6,
  HorseBL = 7,
  HorseBR = 8,
  Sway = 9,
  Flag = 10,
  SiegeArm = 11,
  Wheel = 12,
  Spin = 13,
}

export interface PartOpts {
  color: number;
  part?: Part;
  pivot?: [number, number, number];
  /** Team colour: 0 none; 1 the player's colour; 0.25-1 a darker shade of it; 1-2 a lighter one. */
  team?: number;
  /** Emissive-ish brightness multiplier baked into colour. */
  shade?: number;
  /** Surface texture drawn over the colour (see Surf and textures.ts). */
  surf?: Surf;
}

/** Generated surface textures; each is one layer of the surface texture set (layer = Surf - 1). */
export const enum Surf {
  None = 0,
  Wood = 1,
  Brick = 2,
  Stone = 3,
  Tile = 4,
  Thatch = 5,
  Canvas = 6,
}

/**
 * Accumulates transformed primitives into one merged, non-indexed geometry
 * with attributes: position, normal, color, part, pivot, team, surf.
 * One merged geometry per model keeps every model to a single draw call when instanced.
 */
export class ModelBuilder {
  private pos: number[] = [];
  private nrm: number[] = [];
  private col: number[] = [];
  private part: number[] = [];
  private piv: number[] = [];
  private team: number[] = [];
  private surf: number[] = [];
  /** Primitive index per vertex (each add() is one primitive), used to separate coplanar faces. */
  private prim: number[] = [];
  private primCount = 0;
  private m = new THREE.Matrix4();

  add(geo: THREE.BufferGeometry, opts: PartOpts, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1): this {
    const g = geo.index ? geo.toNonIndexed() : geo;
    this.m.compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(sx, sy, sz));
    g.applyMatrix4(this.m);
    g.computeVertexNormals();
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    const c = new THREE.Color(opts.color);
    if (opts.shade) c.multiplyScalar(opts.shade);
    const id = this.primCount++;
    // Subtle per-face variation so flat colours read as material.
    for (let i = 0; i < p.count; i++) {
      this.prim.push(id);
      this.pos.push(p.getX(i), p.getY(i), p.getZ(i));
      this.nrm.push(n.getX(i), n.getY(i), n.getZ(i));
      const v = 0.94 + (((i / 3) | 0) % 3) * 0.03;
      this.col.push(c.r * v, c.g * v, c.b * v);
      this.part.push(opts.part ?? Part.Static);
      const pv = opts.pivot ?? [0, 0, 0];
      this.piv.push(pv[0], pv[1], pv[2]);
      this.team.push(opts.team ?? 0);
      this.surf.push(opts.surf ?? 0);
    }
    if (g !== geo) g.dispose();
    geo.dispose();
    return this;
  }

  box(w: number, h: number, d: number, o: PartOpts, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0): this {
    return this.add(new THREE.BoxGeometry(w, h, d), o, x, y, z, rx, ry, rz);
  }
  cyl(rt: number, rb: number, h: number, seg: number, o: PartOpts, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0): this {
    return this.add(new THREE.CylinderGeometry(rt, rb, h, seg), o, x, y, z, rx, ry, rz);
  }
  cone(r: number, h: number, seg: number, o: PartOpts, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0): this {
    return this.add(new THREE.ConeGeometry(r, h, seg), o, x, y, z, rx, ry, rz);
  }
  sphere(r: number, o: PartOpts, x = 0, y = 0, z = 0, sx = 1, sy = 1, sz = 1, detail = 1): this {
    return this.add(new THREE.IcosahedronGeometry(r, detail), o, x, y, z, 0, 0, 0, sx, sy, sz);
  }
  /** Triangular prism (gable roof) spanning width w (x), depth d (z), height h. */
  prism(w: number, h: number, d: number, o: PartOpts, x = 0, y = 0, z = 0, ry = 0): this {
    const s = new THREE.Shape();
    s.moveTo(-w / 2, 0);
    s.lineTo(w / 2, 0);
    s.lineTo(0, h);
    s.lineTo(-w / 2, 0);
    const g = new THREE.ExtrudeGeometry(s, { depth: d, bevelEnabled: false });
    g.translate(0, 0, -d / 2);
    return this.add(g, o, x, y, z, 0, ry, 0);
  }
  /** Four-sided pyramid / hip roof. */
  pyramid(w: number, h: number, d: number, o: PartOpts, x = 0, y = 0, z = 0): this {
    const g = new THREE.ConeGeometry(Math.SQRT1_2, 1, 4, 1);
    g.rotateY(Math.PI / 4);
    g.translate(0, 0.5, 0);
    return this.add(g, o, x, y, z, 0, 0, 0, w, h, d);
  }

  /** Append another builder's geometry, scaled uniformly by `k`, rotated about Y by `ry` and then translated. */
  merge(o: ModelBuilder, dx = 0, dy = 0, dz = 0, ry = 0, k = 1): this {
    const c = Math.cos(ry) * k;
    const sn = Math.sin(ry) * k;
    for (let i = 0; i < o.pos.length; i += 3) {
      const [x, z] = [o.pos[i], o.pos[i + 2]];
      const [px, pz] = [o.piv[i], o.piv[i + 2]];
      this.pos.push(x * c + z * sn + dx, o.pos[i + 1] * k + dy, -x * sn + z * c + dz);
      this.piv.push(px * c + pz * sn + dx, o.piv[i + 1] * k + dy, -px * sn + pz * c + dz);
      const [nx, nz] = [o.nrm[i], o.nrm[i + 2]];
      this.nrm.push((nx * c + nz * sn) / k, o.nrm[i + 1], (-nx * sn + nz * c) / k);
    }
    for (const v of o.col) this.col.push(v);
    for (const v of o.part) this.part.push(v);
    for (const v of o.team) this.team.push(v);
    for (const v of o.surf) this.surf.push(v);
    const base = this.primCount;
    for (const v of o.prim) this.prim.push(base + v);
    this.primCount += o.primCount;
    return this;
  }

  build(): THREE.BufferGeometry {
    separateCoplanar(this.pos, this.col, this.part, this.team, this.prim, this.primCount);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('part', new THREE.Float32BufferAttribute(this.part, 1));
    g.setAttribute('pivot', new THREE.Float32BufferAttribute(this.piv, 3));
    g.setAttribute('team', new THREE.Float32BufferAttribute(this.team, 1));
    g.setAttribute('surf', new THREE.Float32BufferAttribute(this.surf, 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

/**
 * Z-fighting guard. Where faces of two different primitives lie in the same plane, face the same way,
 * overlap and differ in colour, the GPU cannot decide which is in front and the surface flickers.
 * For each such pair the smaller primitive (the trim, frame, stripe or other detail) is moved
 * outward along the shared normal by a few millimetres at game scale, so it reliably draws on top.
 */
const NUDGE = 0.004;
function separateCoplanar(pos: number[], col: number[], part: number[], team: number[], prim: number[], primCount: number): void {
  if (primCount < 2) return;
  // Primitive sizes (bounding-box volume, with a floor so flat pieces still rank by area).
  const lo = new Float64Array(primCount * 3).fill(Infinity);
  const hi = new Float64Array(primCount * 3).fill(-Infinity);
  for (let v = 0; v < prim.length; v++) {
    const k = prim[v] * 3;
    for (let a = 0; a < 3; a++) {
      lo[k + a] = Math.min(lo[k + a], pos[v * 3 + a]);
      hi[k + a] = Math.max(hi[k + a], pos[v * 3 + a]);
    }
  }
  const size = new Float64Array(primCount);
  for (let i = 0; i < primCount; i++) {
    const k = i * 3;
    size[i] = Math.max(hi[k] - lo[k], 0.01) * Math.max(hi[k + 1] - lo[k + 1], 0.01) * Math.max(hi[k + 2] - lo[k + 2], 0.01);
  }
  for (let pass = 0; pass < 4; pass++) {
    type T = { i: number; nx: number; ny: number; nz: number; d: number; b: number[] };
    const buckets = new Map<number, T[]>();
    for (let t = 0; t < prim.length / 3; t++) {
      const v = t * 3;
      if (part[v] !== 0) continue; // animated parts move against each other anyway
      const ax = pos[v * 3], ay = pos[v * 3 + 1], az = pos[v * 3 + 2];
      const ux = pos[v * 3 + 3] - ax, uy = pos[v * 3 + 4] - ay, uz = pos[v * 3 + 5] - az;
      const wx = pos[v * 3 + 6] - ax, wy = pos[v * 3 + 7] - ay, wz = pos[v * 3 + 8] - az;
      let nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
      const len = Math.hypot(nx, ny, nz);
      if (len < 1e-7) continue;
      nx /= len; ny /= len; nz /= len;
      const d = nx * ax + ny * ay + nz * az;
      const bx = pos[v * 3 + 3], by = pos[v * 3 + 4], bz = pos[v * 3 + 5], cx = pos[v * 3 + 6], cy = pos[v * 3 + 7], cz = pos[v * 3 + 8];
      const box = [Math.min(ax, bx, cx), Math.max(ax, bx, cx), Math.min(ay, by, cy), Math.max(ay, by, cy), Math.min(az, bz, cz), Math.max(az, bz, cz)];
      // Each face goes into two neighbouring distance buckets so faces at almost the same distance
      // always share one (model dimensions are round numbers that often sit on a bucket boundary).
      const kd = Math.floor(d / 0.002);
      const kn = ((Math.round(nx * 50) + 50) * 101 + (Math.round(ny * 50) + 50)) * 101 + (Math.round(nz * 50) + 50);
      for (let k = kd; k <= kd + 1; k++) {
        const key = kn * 1e6 + k;
        (buckets.get(key) ?? buckets.set(key, []).get(key)!).push({ i: t, nx, ny, nz, d, b: box });
      }
    }
    // Per primitive, the outward directions it must move in (deduplicated).
    const moves = new Map<number, [number, number, number][]>();
    for (const list of buckets.values()) {
      if (list.length < 2) continue;
      for (let a = 0; a < list.length; a++)
        for (let b = a + 1; b < list.length; b++) {
          const A = list[a], B = list[b];
          const pa = prim[A.i * 3], pb = prim[B.i * 3];
          if (pa === pb) continue;
          const ab = A.b, bb = B.b;
          if (ab[1] < bb[0] - 0.002 || bb[1] < ab[0] - 0.002 || ab[3] < bb[2] - 0.002 || bb[3] < ab[2] - 0.002 || ab[5] < bb[4] - 0.002 || bb[5] < ab[4] - 0.002) continue;
          if (Math.abs(A.d - B.d) > 0.0015 || A.nx * B.nx + A.ny * B.ny + A.nz * B.nz < 0.999) continue;
          // Identical surfaces (same colour and team flag) cannot flicker visibly; anything else can.
          const va = A.i * 9, vb = B.i * 9;
          const identical = col[va] === col[vb] && col[va + 1] === col[vb + 1] && col[va + 2] === col[vb + 2] && (team[A.i * 3] > 0.5) === (team[B.i * 3] > 0.5);
          if (identical) continue;
          if (!trianglesOverlap(pos, A.i, B.i, A.nx, A.ny, A.nz)) continue;
          const small = size[pa] <= size[pb] ? pa : pb;
          const list2 = moves.get(small) ?? moves.set(small, []).get(small)!;
          if (!list2.some((m) => m[0] * A.nx + m[1] * A.ny + m[2] * A.nz > 0.99)) list2.push([A.nx, A.ny, A.nz]);
        }
    }
    if (moves.size === 0) return;
    // Push out only the side of the primitive facing each conflict (vertices beyond its centre in
    // that direction). A part flush with a wall at both ends grows at both ends instead of the two
    // nudges cancelling out.
    for (let v = 0; v < prim.length; v++) {
      const dirs = moves.get(prim[v]);
      if (!dirs) continue;
      const k = prim[v] * 3;
      const cx = (lo[k] + hi[k]) / 2, cy = (lo[k + 1] + hi[k + 1]) / 2, cz = (lo[k + 2] + hi[k + 2]) / 2;
      for (const [x, y, z] of dirs) {
        if ((pos[v * 3] - cx) * x + (pos[v * 3 + 1] - cy) * y + (pos[v * 3 + 2] - cz) * z <= 1e-6) continue;
        pos[v * 3] += x * NUDGE;
        pos[v * 3 + 1] += y * NUDGE;
        pos[v * 3 + 2] += z * NUDGE;
      }
    }
    // Refresh bounds for the second pass.
    for (const p of moves.keys()) {
      const k = p * 3;
      lo[k] = lo[k + 1] = lo[k + 2] = Infinity;
      hi[k] = hi[k + 1] = hi[k + 2] = -Infinity;
    }
    for (let v = 0; v < prim.length; v++) {
      if (!moves.has(prim[v])) continue;
      const k = prim[v] * 3;
      for (let a = 0; a < 3; a++) {
        lo[k + a] = Math.min(lo[k + a], pos[v * 3 + a]);
        hi[k + a] = Math.max(hi[k + a], pos[v * 3 + a]);
      }
    }
  }
}

/** Whether two coplanar triangles overlap by more than a sliver (2D separating-axis test). */
function trianglesOverlap(pos: number[], ta: number, tb: number, nx: number, ny: number, nz: number): boolean {
  // Build a 2D basis in the plane.
  let ux = 1, uy = 0, uz = 0;
  if (Math.abs(nx) > 0.9) { ux = 0; uy = 1; }
  const dot = ux * nx + uy * ny + uz * nz;
  ux -= dot * nx; uy -= dot * ny; uz -= dot * nz;
  const ul = Math.hypot(ux, uy, uz);
  ux /= ul; uy /= ul; uz /= ul;
  const vx = ny * uz - nz * uy, vy = nz * ux - nx * uz, vz = nx * uy - ny * ux;
  const tri = (t: number) => [0, 1, 2].map((k) => {
    const o = (t * 3 + k) * 3;
    return [pos[o] * ux + pos[o + 1] * uy + pos[o + 2] * uz, pos[o] * vx + pos[o + 1] * vy + pos[o + 2] * vz];
  });
  const A = tri(ta);
  const B = tri(tb);
  const EPS = 1e-3; // ignore triangles that merely share an edge
  for (const T of [A, B])
    for (let e = 0; e < 3; e++) {
      const [x1, y1] = T[e];
      const [x2, y2] = T[(e + 1) % 3];
      const axx = -(y2 - y1), axy = x2 - x1;
      const al = Math.hypot(axx, axy);
      if (al < 1e-9) continue;
      const proj = (P: number[][]) => P.map((p) => (p[0] * axx + p[1] * axy) / al);
      const pa = proj(A), pb = proj(B);
      if (Math.max(...pa) - EPS <= Math.min(...pb) || Math.max(...pb) - EPS <= Math.min(...pa)) return false;
    }
  return true;
}
